import { FC, useState, useEffect, useMemo } from 'react';
import { Modal, Button, TextInput, Select, Label, Spinner, Alert } from '../../';
import stewardReportServices from '../../../services/stewardReportServices';
import { JobInfo } from '../../../services/dtos';

interface CreateReportModalProps {
  show: boolean;
  onClose: () => void;
  onSuccess: (headerId: number) => void;
}

type Step = 1 | 2 | 3;

export const CreateReportModal: FC<CreateReportModalProps> = ({
  show,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Job
  const [jobId, setJobId] = useState('');
  const [jobInfo, setJobInfo] = useState<JobInfo | null>(null);
  const [jobInfoLoading, setJobInfoLoading] = useState(false);
  const [jobInfoError, setJobInfoError] = useState<string | null>(null);
  const [authorizedJobs, setAuthorizedJobs] = useState<JobInfo[]>([]);
  const [authorizedJobsLoading, setAuthorizedJobsLoading] = useState(false);
  const [jobSearchTerm, setJobSearchTerm] = useState('');

  // Step 2: Week
  const [reportBeginDate, setReportBeginDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  
  // Auto-calculate end date when begin date changes
  useEffect(() => {
    if (reportBeginDate) {
      const startDate = new Date(reportBeginDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6); // 7 days total (including start day)
      setReportEndDate(endDate.toISOString().split('T')[0]);
    } else {
      setReportEndDate('');
    }
  }, [reportBeginDate]);

  // Step 3: Report-to
  const [reportToLocation, setReportToLocation] = useState('');
  const [reportToPerson, setReportToPerson] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [customPerson, setCustomPerson] = useState('');

  const svc = new stewardReportServices();

  // Fetch authorized jobs when modal opens
  useEffect(() => {
    if (show && authorizedJobs.length === 0 && !authorizedJobsLoading) {
      setAuthorizedJobsLoading(true);
      svc.getAuthorizedJobs()
        .then((jobs) => {
          setAuthorizedJobs(jobs);
        })
        .catch((err) => {
          console.error('Error fetching authorized jobs:', err);
          // Don't show error - just allow manual entry
        })
        .finally(() => {
          setAuthorizedJobsLoading(false);
        });
    }
  }, [show, authorizedJobs.length, authorizedJobsLoading]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setStep(1);
      setJobId('');
      setJobInfo(null);
      setReportBeginDate('');
      setReportEndDate('');
      setReportToLocation('');
      setReportToPerson('');
      setCustomLocation('');
      setCustomPerson('');
      setError(null);
      setJobInfoError(null);
      setJobSearchTerm('');
    }
  }, [show]);

  // Filter authorized jobs based on search term
  const filteredAuthorizedJobs = useMemo(() => {
    if (!jobSearchTerm.trim()) {
      return authorizedJobs;
    }
    const term = jobSearchTerm.toLowerCase();
    return authorizedJobs.filter(
      (job) =>
        job.jobId.toLowerCase().includes(term) ||
        (job.employer && job.employer.toLowerCase().includes(term)) ||
        (job.site && job.site.toLowerCase().includes(term))
    );
  }, [authorizedJobs, jobSearchTerm]);

  // Handle job selection from dropdown
  const handleJobSelect = async (selectedJobId: string) => {
    setJobId(selectedJobId);
    setJobSearchTerm('');
    setJobInfoError(null);
    
    if (!selectedJobId) {
      setJobInfo(null);
      return;
    }

    setJobInfoLoading(true);
    try {
      const info = await svc.getJobInfo(selectedJobId);
      setJobInfo(info);
      
      // Pre-populate report-to if only one option
      if (info.reportToLocations.length === 1) {
        setReportToLocation(info.reportToLocations[0]);
      }
      if (info.reportToPersons.length === 1) {
        setReportToPerson(info.reportToPersons[0]);
      }
    } catch (err: any) {
      console.error('Error fetching job info:', err);
      setJobInfoError('Job not found. Please check the Job ID.');
      setJobInfo(null);
    } finally {
      setJobInfoLoading(false);
    }
  };

  // Fetch job info when jobId changes (on blur) - for manual entry
  const handleJobIdBlur = async () => {
    if (!jobId.trim()) {
      setJobInfo(null);
      return;
    }

    setJobInfoLoading(true);
    setJobInfoError(null);

    try {
      const info = await svc.getJobInfo(jobId.trim());
      setJobInfo(info);
      
      // Pre-populate report-to if only one option
      if (info.reportToLocations.length === 1) {
        setReportToLocation(info.reportToLocations[0]);
      }
      if (info.reportToPersons.length === 1) {
        setReportToPerson(info.reportToPersons[0]);
      }
    } catch (err: any) {
      console.error('Error fetching job info:', err);
      setJobInfoError('Job not found. Please check the Job ID.');
      setJobInfo(null);
    } finally {
      setJobInfoLoading(false);
    }
  };

  // Calculate dates for week preview (always 7 days from start date)
  const getWeekDates = (): Date[] => {
    if (!reportBeginDate) return [];
    
    const start = new Date(reportBeginDate);
    const dates: Date[] = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    
    return dates;
  };

  const weekDates = getWeekDates();

  // Validate step 1
  const canProceedStep1 = (): boolean => {
    return !!jobId.trim() && !!jobInfo && !jobInfoError;
  };

  // Validate step 2
  const canProceedStep2 = (): boolean => {
    return !!reportBeginDate;
  };

  // Validate step 3
  const canProceedStep3 = (): boolean => {
    const location = reportToLocation === 'other' ? customLocation : reportToLocation;
    const person = reportToPerson === 'other' ? customPerson : reportToPerson;
    return !!location.trim() && !!person.trim();
  };

  const handleNext = () => {
    if (step === 1 && canProceedStep1()) {
      setStep(2);
    } else if (step === 2 && canProceedStep2()) {
      setStep(3);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const handleCreate = async () => {
    if (!canProceedStep3() || !jobInfo) return;

    setLoading(true);
    setError(null);

    try {
      const location = reportToLocation === 'other' ? customLocation : reportToLocation;
      const person = reportToPerson === 'other' ? customPerson : reportToPerson;

      const header = await svc.createHeader({
        jobId: jobId.trim(),
        reportBeginDate,
        reportEndDate,
        reportToLocation: location.trim(),
        reportToPerson: person.trim(),
        status: 'Draft',
      });

      // Details are prepopulated on the backend but not returned
      // The ReportEditorPage will fetch them via getDetails() when it loads
      onSuccess(header.id);
      onClose();
    } catch (err: any) {
      console.error('Error creating report:', err);
      if (err.responseStatus?.errorCode === '403' || err.responseStatus?.errorCode === '401') {
        setError('You do not have permission to create reports for this job.');
      } else {
        setError(err.message || 'Failed to create report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} size="xl">
      <Modal.Header>Create New Steward Report</Modal.Header>
      <Modal.Body>
        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              1
            </div>
            <div className={`w-20 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              2
            </div>
            <div className={`w-20 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              3
            </div>
          </div>
        </div>

        {error && (
          <Alert color="failure" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Step 1: Job Selection */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="jobSelect" value="Select Job *" />
              <div className="mt-1">
                <Select
                  id="jobSelect"
                  value={jobId}
                  onChange={(e) => handleJobSelect(e.target.value)}
                  className="w-full"
                >
                  <option value="">Select a job...</option>
                  {authorizedJobsLoading ? (
                    <option disabled>Loading jobs...</option>
                  ) : (
                    filteredAuthorizedJobs.map((job) => (
                      <option key={job.jobId} value={job.jobId}>
                        {job.jobId} {job.employer ? `- ${job.employer}` : ''} {job.site ? `(${job.site})` : ''}
                      </option>
                    ))
                  )}
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="jobSearch" value="Search by Job (Optional)" />
              <div className="mt-1 flex gap-2">
                <TextInput
                  id="jobSearch"
                  value={jobSearchTerm}
                  onChange={(e) => setJobSearchTerm(e.target.value)}
                  placeholder="Search by Job ID, Employer, or Site..."
                  className="flex-1"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Filter the job list above or enter a Job ID manually below
              </p>
            </div>

            <div>
              <Label htmlFor="jobId" value="Or Enter Job ID Manually" />
              <div className="mt-1 flex gap-2">
                <TextInput
                  id="jobId"
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value)}
                  onBlur={handleJobIdBlur}
                  placeholder="Enter Job ID"
                  className="flex-1"
                />
                {jobInfoLoading && <Spinner size="sm" />}
              </div>
              {jobInfoError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{jobInfoError}</p>
              )}
            </div>

            {jobInfo && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                <div>
                  <span className="font-semibold">Employer:</span> {jobInfo.employer}
                </div>
                <div>
                  <span className="font-semibold">Site:</span> {jobInfo.site}
                </div>
                <div>
                  <span className="font-semibold">Payroll Company:</span> {jobInfo.payrollCompany}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Week Selection */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="beginDate" value="Start Date *" />
              <TextInput
                id="beginDate"
                type="date"
                value={reportBeginDate}
                onChange={(e) => setReportBeginDate(e.target.value)}
                className="mt-1"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Report will automatically cover 7 days from the start date
              </p>
            </div>

            {reportEndDate && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <p className="text-sm">
                  <span className="font-semibold">End Date:</span>{' '}
                  {new Date(reportEndDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            )}

            {weekDates.length > 0 && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="font-semibold mb-2">Week Preview (7 days):</p>
                <div className="grid grid-cols-7 gap-2 text-sm">
                  {weekDates.map((date, idx) => (
                    <div key={idx} className="text-center">
                      <div className="font-medium">
                        {date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Report-to Selection */}
        {step === 3 && jobInfo && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="location" value="Report-to Location *" />
              <Select
                id="location"
                value={reportToLocation}
                onChange={(e) => setReportToLocation(e.target.value)}
                className="mt-1"
              >
                <option value="">Select location...</option>
                {jobInfo.reportToLocations.map((loc, idx) => (
                  <option key={idx} value={loc}>
                    {loc}
                  </option>
                ))}
                <option value="other">Other...</option>
              </Select>
              {reportToLocation === 'other' && (
                <TextInput
                  value={customLocation}
                  onChange={(e) => setCustomLocation(e.target.value)}
                  placeholder="Enter custom location"
                  className="mt-2"
                />
              )}
            </div>

            <div>
              <Label htmlFor="person" value="Report-to Person *" />
              <Select
                id="person"
                value={reportToPerson}
                onChange={(e) => setReportToPerson(e.target.value)}
                className="mt-1"
              >
                <option value="">Select person...</option>
                {jobInfo.reportToPersons.map((person, idx) => (
                  <option key={idx} value={person}>
                    {person}
                  </option>
                ))}
                <option value="other">Other...</option>
              </Select>
              {reportToPerson === 'other' && (
                <TextInput
                  value={customPerson}
                  onChange={(e) => setCustomPerson(e.target.value)}
                  placeholder="Enter custom person"
                  className="mt-2"
                />
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {step > 1 && (
          <Button color="gray" onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}
        {step < 3 ? (
          <Button onClick={handleNext} disabled={loading || (step === 1 && !canProceedStep1()) || (step === 2 && !canProceedStep2())}>
            Next
          </Button>
        ) : (
          <Button onClick={handleCreate} disabled={loading || !canProceedStep3()}>
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Creating...
              </>
            ) : (
              'Create Draft'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

